<template>
  <div class="follow-up-target-selector">
    <van-nav-bar
      title="选择推荐对象"
      left-arrow
      :border="false"
      @click-left="$router.go(-1)"
    />

    <div class="selector-header-card">
      <div class="selector-title">为当前会员选择异性推荐对象</div>
      <div class="selector-subtitle">默认只展示异性会员，选择后会自动带回姓名、编号和单位</div>
    </div>

    <div class="selector-search-card">
      <van-search
        v-model.trim="keyword"
        shape="round"
        placeholder="搜索姓名或会员编号"
        @search="onRefresh"
        @clear="onRefresh"
      />
    </div>

    <div class="selector-list">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list v-model="loading" :finished="finished" finished-text="没有更多了" @load="onLoad">
          <div
            v-for="item in memberList"
            :key="item.id"
            class="member-card"
            @click="selectMember(item)"
          >
            <div class="member-avatar">
              <img :src="item.avatarLink || defaultAvatar" alt="">
            </div>
            <div class="member-content">
              <div class="member-title-row">
                <div class="member-name">{{ item.nickName ||  formatSerial(item.serialNumber) }}</div>
                <div class="member-serial">#{{ item.serialNumber || '--' }}</div>
              </div>
              <div class="member-meta-row">
                <span>{{ item.hometown || '未知籍贯' }}</span>
                <span>{{ getEducationText(item.education) }}</span>
                <span>{{ item.height ? `${item.height}cm` : '未知身高' }}</span>
              </div>
              <div class="member-company">{{ item.companyName || '未填写单位' }}</div>
            </div>
            <van-icon name="success" class="member-select-icon" />
          </div>
        </van-list>
      </van-pull-refresh>

      <div v-if="!loading && memberList.length === 0" class="empty-state">
        <div class="empty-text">暂无可选会员</div>
        <div class="empty-hint">试试搜索姓名或会员编号</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';
import defaultAvatar from '@/assets/defaultPhoto.png';
import { getEncounterMemberList } from '@/api/user';

export default {
  name: 'FollowUpTargetSelector',
  data() {
    return {
      defaultAvatar,
      keyword: '',
      memberList: [],
      loading: false,
      finished: false,
      refreshing: false,
      page: 1,
      pageSize: 10,
      targetSex: null,
      currentMemberId: ''
    };
  },
  created() {
    this.currentMemberId = this.$route.params.id;
    const currentMemberSex = Number(this.$route.query.currentMemberSex);
    this.targetSex = currentMemberSex === 1 ? 2 : 1;
    this.onRefresh();
  },
  methods: {
    async onRefresh() {
      this.page = 1;
      this.memberList = [];
      this.finished = false;
      await this.onLoad();
      this.refreshing = false;
    },
    async onLoad() {
      if (this.finished || !this.targetSex) {
        this.loading = false;
        this.finished = true;
        return;
      }
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          sex: this.targetSex,
          // 排除情感状态为已领证/已结婚(6)的会员（恋爱ing恢复显示）
          excludeEmotionalStates: [6]
          // 不再过滤是否有红娘跟进、是否已有推荐对象
        };
        const keyword = this.keyword.trim();
        if (keyword) {
          if (/^\d+$/.test(keyword)) {
            params.serialNumber = Number(keyword);
          } else {
            params.name = keyword;
          }
        }
        const res = await getEncounterMemberList(params);
        if (res.code === 20000) {
          const list = (res.data.items || []).filter(item => item.id !== this.currentMemberId);
          this.memberList.push(...list);
          if (list.length < this.pageSize) {
            this.finished = true;
          } else {
            this.page += 1;
          }
        } else {
          this.finished = true;
        }
      } catch (error) {
        this.finished = true;
        Toast.fail('获取会员列表失败');
      } finally {
        this.loading = false;
      }
    },
    selectMember(item) {
      // 传回昵称和编号，不传会员姓名
      const nickName = item.nickName || (item.serialNumber ? String(item.serialNumber).padStart(4, "0") : "");
      // 用 window 临时变量传递数据，go(-1) 返回原页面，避免生成多余历史记录
      window._targetMemberSelected = {
        targetMemberId: item.id,
        targetMemberNickName: nickName,
        targetMemberSerialNumber: item.serialNumber || "",
        targetMemberCompany: item.companyName || ""
      };
      this.$router.go(-1);
    },
    getEducationText(education) {
      const educationData = ['大专', '本科', '硕士', '博士', '高中及以下'];
      return education ? educationData[education - 1] || '未填写学历' : '未填写学历';
    },
    formatSerial(serialNumber) {
      if (!serialNumber) return "0000";
      return String(serialNumber).padStart(4, "0");
    },
  }
};
</script>

<style scoped lang="less">
@import url('../style/theme-variables.less');
@import url('../style/prototype-common.less');

.follow-up-target-selector {
  .prototype-page-shell();
  min-height: 100vh;
}

.selector-header-card,
.selector-search-card,
.member-card,
.empty-state {
  .prototype-card();
}

.selector-header-card {
  margin: 0.8rem;
  padding: 0.9rem;
}

.selector-title {
  font-size: 0.78rem;
  line-height: 1.05rem;
  font-weight: @font-weight-bold;
  color: @text-primary;
}

.selector-subtitle {
  margin-top: 0.28rem;
  font-size: 0.58rem;
  line-height: 0.84rem;
  color: @text-tertiary;
}

.selector-search-card {
  margin: 0 0.8rem 0.8rem;
  padding: 0.18rem 0.2rem;

  /deep/ .van-search {
    padding: 0;
    background: transparent;
  }
}

.selector-list {
  padding: 0 0.8rem 0.8rem;
}

.member-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.8rem;
  margin-bottom: 0.65rem;
}

.member-avatar {
  width: 2.7rem;
  height: 2.7rem;
  border-radius: 0.9rem;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.member-content {
  min-width: 0;
  flex: 1;
}

.member-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.member-name {
  font-size: 0.72rem;
  line-height: 0.98rem;
  font-weight: @font-weight-bold;
  color: @text-primary;
}

.member-serial {
  font-size: 0.52rem;
  color: @primary-color;
}

.member-meta-row {
  margin-top: 0.22rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  font-size: 0.54rem;
  line-height: 0.8rem;
  color: @text-tertiary;
}

.member-company {
  margin-top: 0.24rem;
  font-size: 0.58rem;
  line-height: 0.84rem;
  color: @text-secondary;
}

.member-select-icon {
  font-size: 0.82rem;
  color: @primary-color;
}

.empty-state {
  margin-top: 1rem;
  padding: 1rem 0.8rem;
  text-align: center;
}

.empty-text {
  font-size: 0.7rem;
  line-height: 0.98rem;
  color: @text-primary;
}

.empty-hint {
  margin-top: 0.2rem;
  font-size: 0.56rem;
  line-height: 0.8rem;
  color: @text-tertiary;
}
</style>



// WEBPACK FOOTER //
// src/views/followUpTargetSelector.vue